class SearchModal extends HTMLElement {
  constructor() {
    super();

    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener(
      'keyup',
      (event) => event.code && event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    this.summaryToggle.addEventListener(
      'click',
      this.onSummaryClick.bind(this)
    );

    /*
      DLG-02 - These two buttons are conditional on theme settings. The reset
      button only renders when predictive search is enabled, so the unguarded
      lookup that used to be here would throw inside the constructor the moment a
      merchant unchecked that setting. A throw in a custom element constructor
      stops the element upgrading, which would silently disable every bit of focus
      management below while <details> kept toggling natively - presenting exactly
      as "focus escapes the search popup".
    */
    const closeButton = this.querySelector('button[type="button"]');
    if (closeButton) closeButton.addEventListener('click', this.close.bind(this));

    const resetButton = this.querySelector('button[type="reset"]');
    if (resetButton) resetButton.addEventListener('click', this.reset.bind(this));

    /*
      DLG-02 - The line that used to sit here, `summaryToggle.setAttribute('role',
      'button')`, overrode the native <summary> role. WCAG 2.2 SC 4.1.2.
      <summary> reports expanded/collapsed automatically from its parent <details>;
      replacing the role discarded that, so the control announced "Search, button,
      has popup dialog" and never announced whether the dialog was open. The
      markup already carries aria-haspopup="dialog", so nothing is lost by
      leaving the native role in place.
    */
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open')
      ? this.close()
      : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close();
  }

  open() {
    /*
      DLG-02 - open() and close() are both idempotent because Escape now reaches
      this component by two routes: the shared trap's onEscape (keydown) and the
      pre-existing keyup listener on <details>. Without this guard the second route
      would call removeTrapFocus() again and pop an unrelated dialog's trap off the
      shared stack.
    */
    if (this.detailsContainer.hasAttribute('open')) return;

    setScrollbarWidth();
    setHeaderBottomPosition();

    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);

    this.detailsContainer.setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('search-modal--open');

    /*
      DLG-02 / DLG-03 - The trap container is the <details> element rather than the
      inner `[tabindex="-1"]` wrapper it used to be. That boundary keeps three
      things the user needs inside the trap: the <summary> toggle (which doubles as
      the visible close affordance), the dialog itself, and the click-to-dismiss
      overlay. Everything outside <details> is made inert, so a screen reader
      reading with the virtual cursor can no longer wander into the page behind the
      overlay. WCAG 2.2 SC 2.1.2, 2.4.3, 4.1.2.

      Trap boundaries are recomputed on each Tab by global.js, so predictive-search
      results injected after the dialog opens are inside the trap. Under the
      previous implementation they were not, and Tab past the last result left the
      dialog entirely.
    */
    trapFocus(
      this.detailsContainer,
      this.detailsContainer.querySelector('input:not([type="hidden"])'),
      { modal: true, onEscape: () => this.close() }
    );
  }

  close() {
    if (!this.detailsContainer.hasAttribute('open')) return;

    // No argument: focus returns to the element that opened the dialog. WCAG 2.4.3.
    removeTrapFocus();
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('search-modal--open');
    document.dispatchEvent(new CustomEvent('searchmodal:close'));
  }

  reset(event) {
    event.preventDefault();
    this.querySelector('input[type="search"]').value = '';
  }
}

customElements.define('search-modal', SearchModal);
